import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { FaChevronDown, FaPlus } from 'react-icons/fa6';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import HoverButton from '../../../buttons/HoverButton';
import useSelectedBusiness from '../../../../../hooks/business/useSelectedBusiness';
import useBusinesses from '../../../../../hooks/business/useBusinesses';
import { BUTTON_STYLE, LABEL_STYLE } from './style';
import { businessTypeIcons } from '../../../../../utils/iconUtils';
import Icon from '../../../icons/Icon';
import {
  setSelectedBusiness,
  setSelectedDashboard,
  wipeSelectedBusiness,
} from '../../../../../redux/slices/applicationSlice';

export default function BusinessButton() {
  const business = useSelectedBusiness();
  const businesses = useBusinesses();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  if (!business) return null;

  const handleBusinessSelect = (businessId: number, dashboardId: number) => {
    dispatch(wipeSelectedBusiness());
    dispatch(setSelectedBusiness(businessId));
    dispatch(setSelectedDashboard(dashboardId));
  };

  return (
    <HoverButton hoverable={false}>
      <HoverButton.Button>
        <div className="flex w-fit items-center justify-between gap-2 rounded-[8px] px-3 py-[8px] text-[16px] transition-all hover:bg-secondary-200 group-[.expanded]:bg-secondary-200">
          <div className="flex items-center gap-2">
            <Icon
              icon={businessTypeIcons[business.type]}
              className="h-5 w-5 flex-shrink-0 text-primary"
            />
            <span className="font-medium text-primary-900">{business.name}</span>
          </div>
          <FaChevronDown className="h-3.5 w-3.5 text-primary-300" />
        </div>
      </HoverButton.Button>
      <HoverButton.Div className="mt-2 flex max-h-[75vh] w-full min-w-[250px] flex-col gap-2 overflow-x-hidden overflow-y-scroll rounded-[12px] border border-secondary-200 bg-surface p-3 shadow-lg">
        <div className="w-full text-sm font-semibold text-primary-900">Business</div>
        <div className="w-full border-t border-secondary-200" />
        {(businesses?.filter((b) => b.dashboard) ?? []).map((b) => (
          <div
            key={b.dashboard!.dashboardId}
            onClick={() => handleBusinessSelect(b.businessId, b.dashboard!.dashboardId)}
            className={twMerge(
              BUTTON_STYLE,
              'cursor-pointer items-center justify-between px-3 py-2 leading-[19px]',
              classNames({
                'bg-primary-100 text-primary-900 hover:bg-opacity-100':
                  business.businessId === b.businessId,
              }),
            )}>
            <div className="flex w-full items-center gap-2">
              <Icon icon={businessTypeIcons[b.type]} className="h-5 w-5 flex-shrink-0" />
              <p
                title={b.name}
                className={twMerge(
                  LABEL_STYLE,
                  'line-clamp-2 w-full break-words pr-2 font-medium',
                )}>
                {b.name}
              </p>
            </div>
            {business.businessId === b.businessId && (
              <FaCheck className="h-3 w-3 flex-shrink-0 text-primary-900" />
            )}
          </div>
        ))}
        <div className="w-full border-t border-secondary-200" />
        <div className="flex w-full flex-shrink-0 flex-col">
          <Link to="/onboarding" className={twMerge(BUTTON_STYLE, 'gap-2 px-3 py-2 text-sm')}>
            <FaPlus className="h-4 w-4 text-primary-300" />
            <p className="text-sm font-medium leading-[19px] text-primary-900">
              {t('page.businessOverview.new')}
            </p>
          </Link>
        </div>
      </HoverButton.Div>
    </HoverButton>
  );
}
